import CustomScrollbar from 'components/Common/CustomScrollbar';
import CommitteePublicationNewContent from 'components/Dashboard/CommitteePublication/CommitteePublicationNewContent';
import ConsultationNewContent from 'components/Dashboard/ConsultationsSection/ConsultationNewContent';
import useLoadContentResults from 'components/Dashboard/ContentControl/useLoadContentResults';
import { createURLProps } from 'components/Dashboard/DashboardComponent/DashboardUpdateComponent';
import { createSectionName } from 'components/Dashboard/helpers/controlActiveFilters';
import createMarkup from 'components/Dashboard/helpers/createMarkup';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import KeyUpdateNewContent from 'components/Dashboard/KeyUpdates/KeyUpdateNewContent';
import LegislationNewContent from 'components/Dashboard/Legislation/LegislationNewContent';
import LibraryMaterialMainContent from 'components/Dashboard/LibraryMaterial/LibraryMaterialMainContent';
import ParliamentaryNewContent from 'components/Dashboard/ParliamentaryRecord/ParliamentaryNewContent';
import useRatingUpdatesFunction from 'components/Dashboard/RateUpdatesFeed/useRatingUpdatesFunction';
import TweetItem from 'components/Dashboard/SocialMedia/TweetItem';
import ChangeKeywordsCategory from 'components/Dashboard/utilities/changeKeywordsCategory';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import ReportsInfluenceContent from 'components/Reports/ui/ReportsInfluenceComponent';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';
import transformToPascalCase from 'components/Settings/Utilities/TransformToPascalCase';
import { store } from 'components/Store';
import { useContext, useEffect, useRef, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { isTypingInEditableField } from 'utils/isTypingInEditableField';
import { removeEmptyParagraphs } from 'utils/removeEmptyParagraphs';
import time from 'utils/time';

const RateUpdatesContent = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { keywordsLists } = state;

  const { activeUpdateState, elementsState, scrollBarRef } = props;
  const { activeUpdate, setActiveUpdate } = activeUpdateState;
  const { elements, setElements } = elementsState;
  const [isLoading, setIsLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [scrolling, setScrolling] = useState(false);

  const { ratingUpdateFunction } = useRatingUpdatesFunction();
  const { getNewResult, highlightContent } = useLoadContentResults({});
  const officialReportPaths = [
    'scottish-official-report',
    'welsh-official-report',
    'senedd-committee-transcript',
    'hansard-content',
  ];

  const buttonNotRef = useRef(null);
  const buttonQuiteRef = useRef(null);
  const buttonVeryRef = useRef(null);
  const contentContainer = useRef(null);
  const scrollbarRef = useRef(null);

  const tweetContent = activeUpdate?.type === 'Tweet';

  const callContent = useRef();
  callContent.current = async (source) => {
    try {
      if (!!activeUpdate) {
        setIsLoading(true);
        if (tweetContent) {
          setIsLoading(false);
        } else {
          const url = `${activeUpdate?.contentUrl}`;
          const officialReportCondition = officialReportPaths.some((path) => url?.includes(path));
          const { path, propId, propType } = createURLProps({ url }) ?? {};
          let results = await getNewResult({
            source,
            nameSection: transformToPascalCase(path),
            path,
            propType,
            propId,
          });
          if (!!results) {
            let newResult = { ...results };
            const topicParam = getUrlParam('topic-id');
            if (topicParam) {
              const list = keywordsLists.find(
                (item) => item?.id === (topicParam === 'all' ? null : parseInt(topicParam))
              );
              const contentHightlighted = await highlightContent({
                content: newResult,
                source,
                forceHighlight: true,
                bodyProperty: officialReportCondition && !url.includes('hansard-content') ? 'content' : null,
                titleProperty: null,
                queryHighlight: ChangeKeywordsCategory(list.keywords),
                andKeywords: list.andKeywords,
              });
              newResult = { ...contentHightlighted };
            }
            setContent(newResult);
            setIsLoading(false);
          }
        }
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const source = new AbortController();
    callContent.current(source);
    return () => {
      source.abort('Rate update content cancelled by the user');
    };
  }, [activeUpdate]);

  const rateUpdate = async ({ rating }) => {
    try {
      if (!!activeUpdate) {
        const activeElement = document.querySelector('.rate-active');
        if (activeElement) {
          activeElement.classList.remove('rate-active', 'rate-not', 'rate-quite', 'rate-very');
        }

        const section = elements[createSectionName()];
        const elementIndex = section?.hits?.findIndex((el) => el.objectID === activeUpdate.objectID);
        const element = section?.hits.find((el) => el.objectID === activeUpdate.objectID);
        if (element) {
          setElements({
            ...elements,
            [createSectionName()]: {
              ...section,
              feedItemFlags: [
                ...section?.feedItemFlags?.map((flag, index) => {
                  if (index === elementIndex) {
                    return { ...flag, importanceRating: rating };
                  }
                  return flag;
                }),
              ],
            },
          });

          let targetButton;
          if (rating === 'Not important') {
            targetButton = buttonNotRef.current;
          } else if (rating === 'Quite important') {
            targetButton = buttonQuiteRef.current;
          } else if (rating === 'Very important') {
            targetButton = buttonVeryRef.current;
          }

          if (targetButton && !targetButton.classList.contains('rate-active')) {
            const targetClass = `rate-${rating.split(' ')[0].toLowerCase()}`;
            targetButton.classList.add('rate-active', targetClass);
            const results = await ratingUpdateFunction({
              objectId: activeUpdate.parentObjectID ?? activeUpdate.objectID,
              importanceRating: transformToPascalCase(rating),
            });

            if (results?.status === 200) {
              await new Promise((resolve) => setTimeout(resolve, 300));

              if (scrollBarRef && scrollBarRef.current && elementIndex + 1 < section?.hits?.length) {
                const nextElement = section.hits[elementIndex + 1];
                setActiveUpdate({
                  ...nextElement,
                  importanceRating: section?.feedItemFlags[elementIndex + 1]?.importanceRating,
                });

                setTimeout(() => {
                  const view = scrollBarRef.current.view;
                  const selectedElement = view.querySelector('.rate-update-item-selected');
                  if (selectedElement) {
                    const elementRect = selectedElement.getBoundingClientRect();
                    const viewRect = view.getBoundingClientRect();
                    const marginValue = 8;
                    const relativeTop = elementRect.top - viewRect.top + view.scrollTop - marginValue;
                    scrollBarRef.current.scrollTo(0, relativeTop);
                  }
                }, 100);
              }
              targetButton.classList.remove('rate-active', targetClass);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error rating update:', error);
    }
  };

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (isTypingInEditableField()) {
        return;
      }
      if (event.key.toLowerCase() === 'n') {
        rateUpdate({ rating: 'Not important' });
      } else if (event.key.toLowerCase() === 'q') {
        rateUpdate({ rating: 'Quite important' });
      } else if (event.key.toLowerCase() === 'v') {
        rateUpdate({ rating: 'Very important' });
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [elements, activeUpdate]);

  const rating = activeUpdate?.importanceRating;
  return (
    <>
      <div className='p-4  border-bottom'>
        <RateUpdateButtons
          rating={rating ? TransformFromPascalCase(rating) : ''}
          rateUpdate={rateUpdate}
          buttonRefs={{ buttonNotRef, buttonQuiteRef, buttonVeryRef }}
        />
      </div>
      <div className='bg-blue-hovered flex-grow-1 d-flex flex-column'>
        {isLoading ? (
          <div className='px-4 py-4'>
            <SkeletonTheme baseColor='#f2f2f2'>
              {Array(3)
                .fill()
                .map((item, index) => (
                  <div key={index} className='mb-3'>
                    <Skeleton count={3} height={19} width={`100%`} />
                    <Skeleton height={19} width={`45%`} />
                  </div>
                ))}
            </SkeletonTheme>
          </div>
        ) : (
          <>
            {content?.embargoed && (
              <div className='specific-x-padding-22 pt-3'>
                <p className='my-2 text-red-warning border px-2 d-flex align-items-center justify-content-center specific-y-padding-9'>
                  <span className='icon-clock-thin font-weight-bold' />
                  <span className='specific-left-padding-10'>
                    Embargoed{' '}
                    {!!content?.embargoedUntil ? `until ${time(content?.embargoedUntil).formatEmbargoedDate()}` : ''}
                  </span>
                </p>
              </div>
            )}
            <div className='flex-grow-1'>
              <CustomScrollbar
                ref={scrollbarRef}
                className='simple-scrollbar'
                maximalThumbYSize={100}
                onScrollStart={() => {
                  setScrolling(true);
                }}
                onScrollStop={() => {
                  setScrolling(false);
                }}
              >
                {tweetContent ? (
                  <div className='flex-grow-1'>
                    <div className='w-100 p-4 rate-updates-update-content'>
                      {tweetContent && <TweetItem notShowShare={true} item={activeUpdate} />}
                    </div>
                  </div>
                ) : (
                  <div ref={contentContainer} className='key-update-content rate-updates-update-content'>
                    <div className='specific-top-padding-8'></div>
                    {(() => {
                      const currentNew = content;
                      const contributionId = content?.contributionId;
                      const typeOfContent = content?.objectType;
                      const props = {
                        currentNew: {
                          ...currentNew,
                          body: removeEmptyParagraphs(currentNew?.body),
                          content: removeEmptyParagraphs(currentNew?.content),
                          htmlContent: removeEmptyParagraphs(currentNew?.htmlContent),
                        },
                        contributionId,
                        contentContainer,
                        hideSummary: true,
                        shareButtonWidget: false,
                        scrollbarRef,
                        scrolling,
                        notShowShare: true,
                      };
                      switch (typeOfContent) {
                        case 'Legislation':
                          return <LegislationNewContent {...props} key={`${typeOfContent}-content`} />;
                        case 'ParliamentaryRecord':
                        case 'HansardContent':
                        case 'CountrySpecificParliamentaryRecord':
                        case 'SeneddCommitteeTranscript':
                          return <ParliamentaryNewContent {...props} key={`${typeOfContent}-content`} />;
                        case 'CommonsLibraryArticle':
                          return <LibraryMaterialMainContent {...props} key={`${typeOfContent}-content`} />;
                        case 'KeyUpdate':
                          return <KeyUpdateNewContent {...props} key={`${typeOfContent}-content`} />;
                        case 'Consultation':
                          return <ConsultationNewContent {...props} key={`${typeOfContent}-content`} />;
                        case 'UserContent':
                          return (
                            <div className='pt-3' key={`${typeOfContent}-content`}>
                              <ReportsInfluenceContent {...props} />
                            </div>
                          );
                        case 'Event':
                          return (
                            <div className='p-3' dangerouslySetInnerHTML={createMarkup(currentNew?.description)}></div>
                          );
                        case 'CommitteePublication':
                          return <CommitteePublicationNewContent {...props} key={`${typeOfContent}-content`} />;
                        default:
                          return '';
                      }
                    })()}
                  </div>
                )}
              </CustomScrollbar>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const RateUpdateButtons = ({
  rateUpdate,
  buttonRefs,
  rating,
  contentPage,
  selectedUpdates,
  loadingButton,
  keyboardFunctionality = true,
}) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { team } = state;
  const { newAgencyPlan } = useTopicOrClientWord();
  const { buttonNotRef, buttonQuiteRef, buttonVeryRef } = buttonRefs;
  const buttons = [
    {
      name: 'Not important',
      buttonRef: buttonNotRef,
    },
    {
      name: 'Quite important',
      buttonRef: buttonQuiteRef,
    },
    {
      name: 'Very important',
      buttonRef: buttonVeryRef,
    },
  ];

  const selectedUpdatesCondition = !!selectedUpdates && selectedUpdates?.length > 1;

  return (
    <>
      <p className={`font-weight-bold  mb-1 pr-3 ${contentPage ? 'title-h5' : 'title-h5-m'}`}>
        Rate {selectedUpdatesCondition ? 'these' : 'this'} update{selectedUpdatesCondition ? 's' : ''} based on how
        important {selectedUpdatesCondition ? 'they are' : 'it is'} to{' '}
        {newAgencyPlan ? `${team?.teamName}` : 'your work'}
      </p>
      {keyboardFunctionality && !contentPage && (
        <p className='mb-0 light-50-text'>You can also press N, Q or V on your keyboard</p>
      )}

      <div className='flex-centered gap-15 pt-3 overflow-auto'>
        {buttons.map((button) => {
          const { name, buttonRef } = button;
          return (
            <button
              ref={buttonRef}
              key={name}
              className={`nowrap-item hovered-button flex-centered general-button rounded-pill specific-left-padding-6 specific-right-padding-22 specific-y-padding-3 paragraph-p2 border general-transition ${rating === name ? `rate-active rate-${rating.split(' ')[0].toLowerCase()}` : ''}`}
              onClick={() => rateUpdate({ rating: name })}
            >
              {loadingButton === name ? (
                <i className='fas fa-spinner fa-spin d-inline-block-centered centered-2 mr-2' />
              ) : (
                <span
                  className={`rate-general-indication specific-right-margin-8 rate-${name.split(' ')[0].toLowerCase()}`}
                >
                  <span
                    className={`d-inline-block-centered centered paragraph-p5 centered-${name === 'Quite important' ? '5' : '4'}`}
                  >
                    {name.charAt(0)}
                  </span>
                </span>
              )}
              {name}
            </button>
          );
        })}
      </div>
    </>
  );
};

export { RateUpdateButtons };
export default RateUpdatesContent;
